import React, { useState, useEffect, useMemo } from 'react';
import { base44 } from '@/api/base44Client';
import {
  Loader2, Download, FileSpreadsheet, CheckCircle2, Upload, RefreshCw, AlertCircle, X,
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

// Sage 50 export tab. Lists every sales invoice (SI) and credit note (SC) that
// is NOT yet reconciled to Sage (no sage_invoice_number / sage_credit_number),
// lets the admin tick the ones to export, generates the Audit Trail Transaction
// CSV, and — after the admin has imported it into Sage 50 and verified — marks
// each row reconciled with the Sage number Sage assigned.

const fmt = (n) => `£${(Number(n) || 0).toFixed(2)}`;
const todayISO = () => new Date().toISOString().slice(0, 10);

// Non-sale rows that should never go to the Sage Audit Trail export: test
// orders only. Gift card purchases are real postings — they carry the gift card
// liability to nominal 2105 — so they stay selectable. Admin-generated
// carriage-correction orders are real sales too.
const isNoiseOrder = (o) => /test item/.test(String(o.product_name || '').toLowerCase());

export default function SageExportTab() {
  const { toast } = useToast();
  const [orders, setOrders] = useState([]);
  const [b2b, setB2b] = useState([]);
  const [credits, setCredits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [exporting, setExporting] = useState(false);
  const [recon, setRecon] = useState({}); // {id: sageNumber}
  const [saving, setSaving] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [o, b, c] = await Promise.all([
        base44.entities.Order.list('-created_date', 500),
        base44.entities.B2BOrder.list('-created_date', 500),
        base44.entities.CreditNote.list('-created_date', 500),
      ]);
      setOrders(o || []);
      setB2b(b || []);
      setCredits(c || []);
    } catch (e) {
      toast({ title: 'Load failed', description: e.message, variant: 'destructive' });
    }
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, []);

  // An order needs action if it hasn't been exported via the Audit Trail CSV
  // yet (no sage_exported_at) OR it was exported but hasn't been given a Sage
  // number (no sage_invoice_number). This surfaces real consumer sales that
  // were matched to Sage during reconciliation (so they carry a
  // sage_invoice_number) but haven't had their nominal posting exported.
  // Orders imported FROM Sage (source contains "Sage" — old Sage-imported
  // real sales and the "Misc Sales" monthly Web Sales summaries) are excluded
  // because they already exist in Sage and re-exporting would double-post.
  // Test items and gift-card purchases are excluded as non-sale noise.
  // Pending/failed orders are excluded.
  const retail = useMemo(
    () => orders.filter(
      (o) => (!o.sage_exported_at || !o.sage_invoice_number) && o.status !== 'pending' && o.status !== 'failed' && !isNoiseOrder(o) && !/sage/i.test(String(o.source || ''))
    ),
    [orders]
  );
  // Only B2B orders an admin has explicitly staged (sage_export_ready) appear
  // in the Sage export queue — parsed POs stay out until reconciled via the
  // "Reconcile this PO" action in the B2B portal, so nothing is auto-staged.
  const b2bUnrec = useMemo(() => b2b.filter((o) => !o.sage_invoice_number && o.sage_export_ready === true), [b2b]);
  const creditUnrec = useMemo(() => credits.filter((c) => !c.sage_credit_number), [credits]);

  const key = (type, id) => `${type}:${id}`;
  const toggle = (k) =>
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });
  const toggleAll = (list, type) => {
    const ids = list.map((x) => key(type, x.id));
    const allOn = ids.every((k) => selected.has(k));
    setSelected((prev) => {
      const n = new Set(prev);
      if (allOn) ids.forEach((k) => n.delete(k));
      else ids.forEach((k) => n.add(k));
      return n;
    });
  };

  const exportSelected = async () => {
    const order_ids = retail.filter((o) => selected.has(key('si', o.id))).map((o) => o.id);
    const b2b_order_ids = b2bUnrec.filter((o) => selected.has(key('b2b', o.id))).map((o) => o.id);
    const credit_note_ids = creditUnrec.filter((c) => selected.has(key('sc', c.id))).map((c) => c.id);
    if (!order_ids.length && !b2b_order_ids.length && !credit_note_ids.length) {
      toast({ title: 'Nothing selected', description: 'Tick the rows you want to export', variant: 'destructive' });
      return;
    }
    setExporting(true);
    try {
      const res = await base44.functions.invoke('exportSalesToSage', { order_ids, b2b_order_ids, credit_note_ids });
      if (!res?.data?.success) throw new Error(res?.data?.error || 'Export failed');
      const blob = new Blob([res.data.csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sage_export_${todayISO()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      // Summarise the posting by nominal so the split is visible before the file
      // reaches Sage — rigs, webstore, POS hardware, bookings, food & drink,
      // competitions and gift card liability each reported separately.
      const nominalSummary = Object.entries(res.data.counts.by_nominal || {})
        .map(([code, net]) => `${code} £${Number(net).toFixed(2)}`)
        .join(' · ');
      toast({
        title: 'Sage CSV exported',
        description: `${res.data.counts.rows} rows · ${res.data.counts.si_orders} consumer + ${res.data.counts.si_b2b} B2B + ${res.data.counts.sc_credits} credits${nominalSummary ? ` · ${nominalSummary}` : ''}`,
      });
      setSelected(new Set());
      load();
    } catch (e) {
      toast({ title: 'Export failed', description: e.message, variant: 'destructive' });
    }
    setExporting(false);
  };

  // One-click bulk reconcile: stamp every exported-but-unreconciled order with
  // its own invoice number as the Sage number, so it leaves the export list.
  const markAllExportedAsInSage = async () => {
    const retailPatches = retail
      .filter((o) => o.sage_exported_at && !o.sage_invoice_number)
      .map((o) => ({
        id: o.id,
        sage_invoice_number: String(o.invoice_number || o.id),
        sage_invoice_date: (o.sage_invoice_date || o.created_date || '').slice(0, 10),
      }));
    const b2bPatches = b2bUnrec
      .filter((o) => o.sage_exported_at && !o.sage_invoice_number)
      .map((o) => ({
        id: o.id,
        sage_invoice_number: String(o.invoice_number || o.po_number || o.id),
        sage_invoice_date: (o.sage_invoice_date || o.po_date || o.created_date || '').slice(0, 10),
      }));
    if (!retailPatches.length && !b2bPatches.length) {
      toast({ title: 'Nothing to mark', description: 'No exported invoices are awaiting reconciliation' });
      return;
    }
    if (!window.confirm(`Mark ${retailPatches.length + b2bPatches.length} exported invoices as already in Sage? This removes them from the export list.`)) return;
    setExporting(true);
    try {
      if (retailPatches.length) await base44.entities.Order.bulkUpdate(retailPatches);
      if (b2bPatches.length) await base44.entities.B2BOrder.bulkUpdate(b2bPatches);
      toast({ title: 'Marked as in Sage', description: `${retailPatches.length} consumer + ${b2bPatches.length} B2B reconciled` });
      load();
    } catch (e) {
      toast({ title: 'Failed', description: e.message, variant: 'destructive' });
    }
    setExporting(false);
  };

  const markReconciled = async (type, item) => {
    const val = (recon[item.id] || '').trim();
    if (!val) {
      toast({ title: 'Enter the Sage number first', variant: 'destructive' });
      return;
    }
    setSaving(item.id);
    try {
      if (type === 'sc') {
        await base44.entities.CreditNote.update(item.id, { sage_credit_number: val });
      } else if (type === 'b2b') {
        await base44.entities.B2BOrder.update(item.id, { sage_invoice_number: val, sage_invoice_date: todayISO() });
      } else {
        await base44.entities.Order.update(item.id, { sage_invoice_number: val, sage_invoice_date: todayISO() });
      }
      toast({ title: 'Marked reconciled', description: `${type === 'sc' ? 'Credit' : 'Invoice'} ${val} saved` });
      setRecon((r) => {
        const n = { ...r };
        delete n[item.id];
        return n;
      });
      load();
    } catch (e) {
      toast({ title: 'Failed', description: e.message, variant: 'destructive' });
    }
    setSaving(null);
  };

  const totalCount = retail.length + b2bUnrec.length + creditUnrec.length;
  const selectedCount = selected.size;

  return (
    <div className="space-y-6">
      {/* Info + actions */}
      <div className="border border-white/10 bg-white/[0.02] p-4 space-y-3">
        <p className="text-white/50 text-xs leading-relaxed">
          <span className="text-white/70 font-bold">How it works:</span> Tick the unreconciled sales invoices (SI) and credit notes (SC) below, then{' '}
          <span className="text-white/70">Export to Sage CSV</span>. Import the file into Sage 50 via{' '}
          <span className="text-white/70">File → Import → Audit Trail Transactions</span>. After checking it posted correctly, use{' '}
          <span className="text-white/70">Mark reconciled</span> on each row and enter the Sage invoice/credit number — that removes it from this list so it can't be double-posted.
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-white/40 text-[10px] font-bold tracking-widest">
            {totalCount} UNRECONCILED · {selectedCount} SELECTED
          </span>
          <button
            onClick={exportSelected}
            disabled={exporting || !selectedCount}
            className="inline-flex items-center gap-1.5 bg-primary hover:bg-primary/90 disabled:opacity-40 text-white font-bold text-[10px] tracking-widest px-4 py-2 transition-colors"
          >
            {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} EXPORT TO SAGE CSV
          </button>
          <button
            onClick={markAllExportedAsInSage}
            disabled={exporting}
            title="Stamp every exported invoice with its own number as the Sage number, removing it from this list"
            className="inline-flex items-center gap-1.5 border border-green-400/30 bg-green-400/10 hover:bg-green-400 hover:text-black text-green-400 font-bold text-[10px] tracking-widest px-3 py-2 transition-colors disabled:opacity-50"
          >
            <CheckCircle2 className="w-3.5 h-3.5" /> MARK ALL EXPORTED AS IN SAGE
          </button>
          <button onClick={load} className="inline-flex items-center gap-1.5 border border-white/15 hover:border-white/30 text-white/40 hover:text-white font-bold text-[10px] tracking-widest px-3 py-2 transition-colors">
            <RefreshCw className="w-3.5 h-3.5" /> REFRESH
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : totalCount === 0 ? (
        <div className="text-center py-16 text-white/30 border border-white/10">
          <CheckCircle2 className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm font-bold tracking-widest">EVERYTHING IS RECONCILED</p>
          <p className="text-xs mt-1">No unreconciled sales invoices or credit notes to export.</p>
        </div>
      ) : (
        <>
          {retail.length > 0 && (
            <Section
              title="CONSUMER SALES (SI → WEBS001)"
              count={retail.length}
              allSelected={retail.every((o) => selected.has(key('si', o.id)))}
              onToggleAll={() => toggleAll(retail, 'si')}
              accent="blue"
            >
              {retail.map((o) => (
                <ExportRow
                  key={o.id}
                  type="si"
                  selected={selected.has(key('si', o.id))}
                  onToggle={() => toggle(key('si', o.id))}
                  refNo={o.invoice_number || o.id}
                  date={o.sage_invoice_date || o.created_date}
                  customer={o.customer_name}
                  amount={o.amount}
                  status={o.status}
                  exportedAt={o.sage_exported_at}
                  reconValue={recon[o.id] || ''}
                  onReconChange={(v) => setRecon((r) => ({ ...r, [o.id]: v }))}
                  onMark={() => markReconciled('si', o)}
                  saving={saving === o.id}
                  product={o.product_name}
                />
              ))}
            </Section>
          )}

          {b2bUnrec.length > 0 && (
            <Section
              title="B2B SALES (SI → TRADE ACCOUNT)"
              count={b2bUnrec.length}
              allSelected={b2bUnrec.every((o) => selected.has(key('b2b', o.id)))}
              onToggleAll={() => toggleAll(b2bUnrec, 'b2b')}
              accent="orange"
            >
              {b2bUnrec.map((o) => (
                <ExportRow
                  key={o.id}
                  type="b2b"
                  selected={selected.has(key('b2b', o.id))}
                  onToggle={() => toggle(key('b2b', o.id))}
                  refNo={o.invoice_number || o.po_number || o.id}
                  date={o.po_date || o.sage_invoice_date || o.created_date}
                  customer={o.company_name}
                  amount={o.total_inc_vat}
                  status={o.status}
                  exportedAt={o.sage_exported_at}
                  reconValue={recon[o.id] || ''}
                  onReconChange={(v) => setRecon((r) => ({ ...r, [o.id]: v }))}
                  onMark={() => markReconciled('b2b', o)}
                  saving={saving === o.id}
                  product={o.po_number ? `PO ${o.po_number}` : ''}
                />
              ))}
            </Section>
          )}

          {creditUnrec.length > 0 && (
            <Section
              title="CREDIT NOTES (SC → WEBS001)"
              count={creditUnrec.length}
              allSelected={creditUnrec.every((c) => selected.has(key('sc', c.id)))}
              onToggleAll={() => toggleAll(creditUnrec, 'sc')}
              accent="red"
            >
              {creditUnrec.map((c) => (
                <ExportRow
                  key={c.id}
                  type="sc"
                  selected={selected.has(key('sc', c.id))}
                  onToggle={() => toggle(key('sc', c.id))}
                  refNo={c.credit_note_number}
                  date={c.created_date}
                  customer={c.customer_name}
                  amount={-(Number(c.amount) || 0)}
                  status={c.is_full_refund ? 'full refund' : 'partial'}
                  exportedAt={c.sage_exported_at}
                  reconValue={recon[c.id] || ''}
                  onReconChange={(v) => setRecon((r) => ({ ...r, [c.id]: v }))}
                  onMark={() => markReconciled('sc', c)}
                  saving={saving === c.id}
                  product={c.reason}
                />
              ))}
            </Section>
          )}

        </>
      )}
    </div>
  );
}

function Section({ title, count, allSelected, onToggleAll, accent, children }) {
  const accentClass = accent === 'blue' ? 'text-blue-400' : accent === 'orange' ? 'text-orange-400' : accent === 'green' ? 'text-green-400' : 'text-red-400';
  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <p className={`${accentClass} text-[10px] font-bold tracking-widest`}>{title} ({count})</p>
        <button onClick={onToggleAll} className="text-white/30 hover:text-white text-[10px] font-bold tracking-widest">
          {allSelected ? 'CLEAR' : 'SELECT ALL'}
        </button>
      </div>
      <div className="border border-white/10 divide-y divide-white/5">{children}</div>
    </div>
  );
}

function ExportRow({
  type, selected, onToggle, refNo, date, customer, amount, status, exportedAt, reconValue, onReconChange, onMark, saving, product, hideRecon,
}) {
  const d = date ? new Date(String(date).includes('T') ? date : date + 'T00:00:00') : null;
  const isCredit = type === 'sc';
  return (
    <div className="flex items-center gap-3 px-4 py-3 hover:bg-white/[0.02] transition-colors flex-wrap">
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggle}
        className="w-4 h-4 accent-[var(--primary)] cursor-pointer shrink-0"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`font-mono font-bold text-xs ${isCredit ? 'text-red-400' : type === 'b2b' ? 'text-orange-400' : 'text-blue-400'}`}>
            {refNo || '—'}
          </span>
          <span className="text-[9px] font-bold tracking-widest border border-white/15 text-white/40 px-1.5 py-0.5">
            {isCredit ? 'SC' : 'SI'}
          </span>
          {exportedAt && (
            <span className="text-[9px] font-bold tracking-widest border border-cyan-400/30 text-cyan-400 px-1.5 py-0.5" title={`Exported ${new Date(exportedAt).toLocaleString('en-GB')}`}>
              EXPORTED
            </span>
          )}
        </div>
        <p className="text-white text-xs mt-0.5 truncate">
          {customer || '—'} {product ? <span className="text-white/30">· {product}</span> : null}
        </p>
      </div>
      <span className="text-white/40 text-xs hidden sm:block">{d ? d.toLocaleDateString('en-GB') : '—'}</span>
      <span className="text-[9px] text-white/30 font-bold tracking-widest uppercase hidden md:block">{status}</span>
      <span className={`font-bold text-xs ${Number(amount) < 0 ? 'text-red-400' : 'text-white'}`}>{fmt(amount)}</span>
      {!hideRecon && (
        <div className="flex items-center gap-1 shrink-0">
          <input
            value={reconValue}
            onChange={(e) => onReconChange(e.target.value)}
            placeholder="Sage #"
            className="w-20 bg-[#0d0d0f] border border-white/10 text-white text-[10px] px-2 py-1 focus:border-primary/60 placeholder:text-white/20"
          />
          <button
            onClick={onMark}
            disabled={saving}
            title="Mark this invoice reconciled with the Sage number"
            className="inline-flex items-center justify-center w-7 h-7 border border-green-400/30 bg-green-400/10 hover:bg-green-400 hover:text-black text-green-400 transition-colors disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
          </button>
        </div>
      )}
    </div>
  );
}